import { useChatbotStore } from '../store/chatbot.store';
import { useCollectionCache } from './collectionCache.service';
import { createKeywordExtractionPrompt } from '../prompts/keywordExtractionPrompt';
import { useFilterStore } from '../store/filter.store';
import { callGeminiAPI } from './chatbotSharedService';

export interface ExtractedKeywords {
  keywords?: string[];
  filters?: any;
  logic?: {
    tag?: string;
    tag2?: string;
    genre?: string;
  };
  confidence?: { [key: string]: number };
  naturalLanguageResponse?: string;
  secondaryMatches?: Array<{
    label: string;
    collection: string;
    filter: any;  // Using any for now as we'll need to import SearchFilter type if we want to be more specific
  }>;
  directFilters?: {
    tags?: string[];
    tags2?: string[];
    genres?: string[];
    keys?: string[];
    agencies?: string[];
    clients?: string[];
    producers?: string[];
    studios?: string[];
    composers?: string[];
    creativeDirectors?: string[];
    sessionArtists?: string[];
    license?: string;
    logic?: {
      tag?: string;
      tag2?: string;
      genre?: string;
    };
  };
}

function parseFindATrackJson(json: any): ExtractedKeywords {
  // Map the Gemini JSON output to the ExtractedKeywords structure expected by Find a Track
  // This function can be extended as needed for Find a Track's unique needs
  return {
    keywords: json.keywords,
    filters: json.filters,
    logic: json.logic,
    confidence: json.confidence,
    secondaryMatches: json.secondaryMatches,
    // naturalLanguageResponse will be set separately
  };
}

class KeywordExtractor {
  /**
   * Converts the current filterState from the global store to JSON format
   * @returns JSON representation of the current filters
   */
  getCurrentFiltersAsJSON() {
    const { filterState } = useFilterStore.getState();
    return convertFilterStateToJSON(filterState);
  }

  async extractKeywords(query: string): Promise<ExtractedKeywords> {
    const { config } = useChatbotStore.getState();
    if (!config || !config.apiKey || !config.enabled) {
      throw new Error('Chatbot is not configured or enabled');
    }

    // Get collection cache state
    const collectionCache = useCollectionCache.getState();
    if (!collectionCache.isInitialized) {
      throw new Error('Collection cache is not initialized');
    }

    // Extract only genres and tags data, simplified to only include displayName
    const collectionData = {
      genres: collectionCache.genres.map(item => ({ displayName: item.displayName })),
      tags: collectionCache.tags.map(item => ({ displayName: item.displayName }))
    };

    // Get current filters as JSON from global store
    const currentFiltersJSON = this.getCurrentFiltersAsJSON();

    // Get filtered chat history from chatbot store (excludes messages before reset flags)
    const chatHistory = useChatbotStore.getState().getFilteredChatHistory();
    // Create prompt with collection data, current filters JSON, and chat history
    const prompt = createKeywordExtractionPrompt(query, collectionData, currentFiltersJSON, chatHistory);
    
    //Prompt sent to Gemini
    //console.log('Prompt sent to Gemini:', prompt);

    try {
      const geminiResult = await callGeminiAPI(prompt, config.apiKey);
      //console.log('Raw Gemini API response:', geminiResult);
      const mapped = parseFindATrackJson(geminiResult.json);
      let naturalLanguageResponse = geminiResult.naturalLanguage;

      // Normalize logic.tag and logic.genre to lowercase if present
      if (mapped.logic) {
        if (typeof mapped.logic.tag === 'string') {
          mapped.logic.tag = mapped.logic.tag.toLowerCase();
        }
        if (typeof mapped.logic.genre === 'string') {
          mapped.logic.genre = mapped.logic.genre.toLowerCase();
        }
      }

      // Remove "Natural language response:" prefix if present
      if (naturalLanguageResponse.startsWith('Natural language response:')) {
        naturalLanguageResponse = naturalLanguageResponse.replace('Natural language response:', '').trim();
      }
      // Remove any remaining quotes if present
      if (naturalLanguageResponse.startsWith('"') && naturalLanguageResponse.endsWith('"')) {
        naturalLanguageResponse = naturalLanguageResponse.slice(1, -1);
      }

      return {
        ...mapped,
        naturalLanguageResponse
      };
    } catch (error) {
      console.error('Error extracting keywords:', error);
      throw error;
    }
  }
}

// Utility to convert filterState to JSON format matching Gemini output
function convertFilterStateToJSON(filterState: any): {
  keywords: string[];
  filters: {
    date?: string[];
    client?: string;
    bpm?: string;
    key?: string;
    studio?: string;
    agency?: string;
    producer?: string;
    license?: string;
    tags?: string[];
    tags2?: string[];
    genres?: string[];
    sessionartists?: string[];
    creativedirectors?: string[];
    composers?: string[];
  };
  logic: {
    genre?: string;
    tag?: string;
    tag2?: string;
  };
} {
  const result = {
    keywords: [] as string[],
    filters: {
      date: [] as string[],
      client: '',
      bpm: '',
      key: '',
      studio: '',
      agency: '',
      producer: '',
      license: '',
      tags: [] as string[],
      tags2: [] as string[],
      genres: [] as string[],
      sessionartists: [] as string[],
      creativedirectors: [] as string[],
      composers: [] as string[]
    },
    logic: {
      genre: '',
      tag: '',
      tag2: ''
    }
  };

  if (!filterState) return result;

  // Tags (multi)
  if (filterState.multi?.active && Array.isArray(filterState.multi.data)) {
    result.filters.tags = filterState.multi.data.map((tag: any) => tag.label).filter(Boolean);
  }

  // Tags2 (Required Elements)
  if (filterState.tags2?.active && Array.isArray(filterState.tags2.data)) {
    result.filters.tags2 = filterState.tags2.data.map((tag: any) => tag.label).filter(Boolean);
  }

  // Genres
  if (filterState.genres?.active && Array.isArray(filterState.genres.data)) {
    result.filters.genres = filterState.genres.data.map((genre: any) => genre.label).filter(Boolean);
  }

  // Logic
  if (filterState.multi?.searchOperator) {
    result.logic.tag = filterState.multi.searchOperator.toLowerCase();
  }
  if (filterState.tags2?.searchOperator) {
    result.logic.tag2 = filterState.tags2.searchOperator.toLowerCase();
  }
  if (filterState.genres?.searchOperator) {
    result.logic.genre = filterState.genres.searchOperator.toLowerCase();
  }

  // BPM
  if (filterState.bpm?.active) {
    if (filterState.bpm.filterType === 'range' && (filterState.bpm.range?.min || filterState.bpm.range?.max)) {
      const min = filterState.bpm.range?.min || '';
      const max = filterState.bpm.range?.max || '';
      result.filters.bpm = `${min}-${max}`;
    } else if (filterState.bpm.exact) {
      result.filters.bpm = filterState.bpm.exact.toString();
    }
  }

  // Keys (only first one since it's a string field)
  if (filterState.keys?.active && Array.isArray(filterState.keys.data) && filterState.keys.data.length > 0) {
    result.filters.key = filterState.keys.data[0].label;
  }

  // Studios (only first one since it's a string field)
  if (filterState.studios?.active && Array.isArray(filterState.studios.data) && filterState.studios.data.length > 0) {
    result.filters.studio = filterState.studios.data[0].label;
  }

  // Agencies (only first one since it's a string field)
  if (filterState.agencies?.active && Array.isArray(filterState.agencies.data) && filterState.agencies.data.length > 0) {
    result.filters.agency = filterState.agencies.data[0].label;
  }

  // Clients (only first one since it's a string field)
  if (filterState.clients?.active && Array.isArray(filterState.clients.data) && filterState.clients.data.length > 0) {
    result.filters.client = filterState.clients.data[0].label;
  }

  // Producers (only first one since it's a string field)
  if (filterState.producers?.active && Array.isArray(filterState.producers.data) && filterState.producers.data.length > 0) {
    result.filters.producer = filterState.producers.data[0].label;
  }

  // Composers (array field)
  if (filterState.composers?.active && Array.isArray(filterState.composers.data)) {
    result.filters.composers = filterState.composers.data.map((composer: any) => composer.label).filter(Boolean);
  }

  // Session Artists (array field) - need to check the correct field name
  if (filterState.sessionArtists?.active && Array.isArray(filterState.sessionArtists.data)) {
    result.filters.sessionartists = filterState.sessionArtists.data.map((artist: any) => artist.label).filter(Boolean);
  }

  // Creative Directors (array field) - need to check the correct field name  
  if (filterState.creativeDirectors?.active && Array.isArray(filterState.creativeDirectors.data)) {
    result.filters.creativedirectors = filterState.creativeDirectors.data.map((director: any) => director.label).filter(Boolean);
  }

  // License
  if (filterState.license?.active && filterState.license.value !== undefined) {
    result.filters.license = filterState.license.value ? 'licensed' : 'unlicensed';
  }

  // Date
  if (filterState.date?.active && filterState.date.from && filterState.date.to) {
    const fromDate = new Date(filterState.date.from);
    const toDate = new Date(filterState.date.to);
    // Format as "1 May 2015"
    const formatDate = (date: Date) => {
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'long' });
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    };
    result.filters.date = [formatDate(fromDate), formatDate(toDate)];
  }

  // Text search - this becomes a keyword since it's not in the filters object
  if (filterState.text?.active && filterState.text.value) {
    result.keywords.push(filterState.text.value);
  }

  return result;
}

export const keywordExtractor = new KeywordExtractor();
export { convertFilterStateToJSON };

/**
 * Maps a keywords array to selector fields using the cached collection data.
 * Returns an object with matched fields (genres, tags, etc.) and unmatched keywords.
 */
export function mapKeywordsToSelectors(keywords: string[], collectionCache: any) {
  if (!Array.isArray(keywords) || !collectionCache) return { matches: {}, unmatched: keywords };
  const matches: Record<string, any[]> = {};
  const unmatched: string[] = [];
  // Remove genres and tags from the collections to be searched
  const fieldMap = {
    // genres: 'genres', // removed
    // tags: 'tags', // removed
    agency: 'agency',
    client: 'client',
    producer: 'producer',
    creativedirector: 'creativedirector',
    composers: 'composers',
    sessionartists: 'sessionartists',
    studio: 'studio',
    flightingplatform: 'flightingplatform',
    backuplocation: 'backuplocation',
    key: 'keys',
    bpm: 'bpm',
  };

  for (const keyword of keywords) {
    let found = false;
    const lower = String(keyword).toLowerCase();
    for (const [field, collectionKey] of Object.entries(fieldMap)) {
      const collection = collectionCache[collectionKey] || collectionCache[field] || [];
      const fieldMatches = collection.filter((item: any) => {
        const display = String(item.displayName).toLowerCase();
        // For genres/tags: skip (not searched here)
        // For other fields: word-boundary match only
        const wordBoundaryRegex = new RegExp(`\\b${lower}\\b`, 'i');
        return wordBoundaryRegex.test(display);
      });
      if (fieldMatches.length > 0) {
        if (!matches[field]) matches[field] = [];
        matches[field].push(...fieldMatches);
        found = true;
      }
    }
    if (!found) {
      unmatched.push(keyword);
    }
  }
  return { matches, unmatched };
} 